/**
 * Why an email did not go out.
 *
 * The sender before MAP 249 (`deliverEmail`) always knew the difference between "nobody has
 * set a Resend key on this deployment" and "Resend answered and refused" — it returned a
 * `via: 'resend' | 'skipped'` that said so. Every caller read `.delivered` and threw the rest
 * away, so a revoked key, an unverified sending domain, a 429 and a socket reset all came out
 * of the product as the same sentence: *"Email is not switched on for this account."*
 *
 * That sentence is a lie in three of those four cases, and it is the expensive kind: it
 * points the shop at a setup step they already did, while the real problem — an account
 * that needs a human — goes unnamed. Worse, the nightly dunning sweep recorded those
 * failures as `'skipped'`, which is the vocabulary for *"we chose not to"*. The count of
 * failed reminders on the Connected services card was therefore always zero, on every
 * deployment, no matter how much mail bounced.
 *
 * So the reason is a value now, and it lives here rather than in `apps/api` because the
 * screen that has to say the sentence is in `apps/web`. One vocabulary, one set of words,
 * one place to change them — four sites drifting apart is the defect, not the symptom.
 */

/**
 * Every way a send can fail, split by **what the shop should do about it** — which is most
 * of what a message like this earns.
 *
 * One member is a deliberate exception, said plainly because a reviewer was right to ask:
 * `throttled` and `unreachable` take the same action (wait, then retry) and the same status
 * (`'failed'`). It keeps its own name for a DIAGNOSTIC reason, not an actionable one — a 429
 * is about the shop's own volume and will recur on the same schedule, while an unreachable
 * mail service is somebody else's afternoon and won't. Collapsing them would also put a 429
 * back with a revoked key, which is one of the four things the ticket named.
 */
export const DELIVERY_FAILURES = [
  /** No address to send to. Nothing about the mail account is wrong. */
  'no-recipient',
  /** No `RESEND_API_KEY` / `EMAIL_FROM`. Never attempted, and the shop already knows. */
  'not-configured',
  /** The mail service answered, and said no: a revoked key, an unverified domain, a
   *  malformed address. Somebody has to go and look at the account. */
  'rejected',
  /** 429. The account is fine and the message is fine; there were too many, too fast. */
  'throttled',
  /**
   * No CONFIRMATION — a socket reset, a DNS failure, a timeout, a 408, any 5xx, or a 2xx
   * whose body did not name a message.
   *
   * ⚠️ The only member that means **we do not know**. Every other failure is a definite
   * negative: nobody was asked, or somebody said no. This one is a request that went out
   * and came back with nothing that identifies a message, and it may well have been
   * accepted first. Treating it as "nothing was sent" is how a customer gets the same
   * invoice twice. See `knownNotSent`.
   *
   * Named for the outcome, not the mechanism: an earlier version of this comment (and of
   * `deliveryProblem`) said "no answer at all", which is false of the 408 and the 5xx that
   * live here. Those ARE answers. What none of them is, is a confirmation.
   */
  'unreachable',
] as const;

export type DeliveryFailure = (typeof DELIVERY_FAILURES)[number];

/**
 * Why an email of a ticket's customer-visible message did not go (`ticket_emails.failure`,
 * ops insights Phase 5b): every `DELIVERY_FAILURES` reason, plus two only that ledger has.
 *
 *  - `no-reply-address`: the shop has set no "Customer replies go to" address. Never sent
 *    without one: the customer's reply would reach the platform's mailbox, not the shop's.
 *  - `interrupted`: a worker died between claiming the row and hearing back from the mail
 *    service. Like `unreachable`, it MAY have gone out, so it is never sent again.
 *
 * ⚠️ One list, three readers: the `ticket_emails_failure_check` CHECK (migration 0038, a
 * second copy SQL cannot import), the zod schema and the panel's sentences. The migration
 * test writes every value through the CHECK.
 */
export const TICKET_EMAIL_FAILURES = [
  ...DELIVERY_FAILURES,
  'no-reply-address',
  'interrupted',
  /**
   * MAP 249: sent through the shop's connected mailbox, which timed out or answered 5xx. A
   * mailbox takes no idempotency key, so it is never retried (spec §2 step 7).
   */
  'unconfirmed',
  /** MAP 249: ServWell kept failing on it before any call (the row-error budget). */
  'internal',
] as const;
export type TicketEmailFailure = (typeof TICKET_EMAIL_FAILURES)[number];

/**
 * The outcome of one send.
 *
 * Deliberately a union rather than `{ delivered: boolean; failure: DeliveryFailure | null }`:
 * with two independent fields, `{ delivered: true, failure: 'rejected' }` typechecks and
 * means nothing, and — the part that actually bit — a caller can read `.delivered` and
 * never be told that `.failure` exists. Narrowing on `delivered` is the only way to reach
 * the reason, so the compiler now asks the question the four call sites forgot to.
 */
export type DeliveryResult =
  /** `providerId`: the mail service's id for the message (Resend's `id`), when it named one. */
  | { delivered: true; failure: null; providerId?: string }
  | { delivered: false; failure: DeliveryFailure };

/**
 * Did the mail service actually get a look at this?
 *
 * This is the line between a row worth telling somebody about and a row that is merely a
 * consequence of a switch being off. A shop on a deployment with no Resend key does not
 * need "47 reminders failed this week" — they know, and every one of those is recoverable
 * the moment a key is set. A shop whose key was revoked on Tuesday needs exactly that
 * number, because nothing else in the product will ever mention it.
 */
export function countsAsFailure(failure: DeliveryFailure): boolean {
  return failure === 'rejected' || failure === 'throttled' || failure === 'unreachable';
}

/**
 * Do we actually KNOW nothing went out?
 *
 * ⭐ Four of the five are definite: nobody to send to, no key to send with, a refusal, a
 * rate limit. `unreachable` is not — the request left and nothing came back that identifies
 * a message, and the mail service may have accepted it anyway. Only the send gate's retry (the
 * same bytes under the same idempotency key, inside the provider's window) is not a second
 * email; any other resend is a real second email to a real customer.
 *
 * This exists because the first version of this vocabulary had every surface glue the words
 * *", so nothing was sent."* onto the end of `deliveryProblem`, which asserts a fact about
 * `unreachable` that nobody has. A signal with only GOOD and BAD silently files every
 * UNKNOWN under one of them; the useful ones say which.
 */
export function knownNotSent(failure: DeliveryFailure): boolean {
  return failure !== 'unreachable';
}

/**
 * The three words the database has for a send.
 *
 * A CONST, not a bare type alias, because `invoice_reminders`, `quote_reminders` and
 * `email_campaign_recipients` each carry a CHECK constraint over exactly these values and
 * all three used to hardcode their own copy of the list. Adding a fourth word to a type
 * alias typechecks everywhere and then throws `invoice_reminders_status_check` in
 * production — which is the repo's own pattern (`ROLES`, `QUOTE_STATES`, `DELIVERY_FAILURES`)
 * existing precisely to prevent, and the one place that had opted out of it.
 */
export const SEND_OUTCOMES = ['sent', 'skipped', 'failed'] as const;
export type SendOutcome = (typeof SEND_OUTCOMES)[number];

/**
 * What a nightly sweep reports for one row — the three above, plus the one the database
 * has no word for.
 *
 * `'due'` is PREVIEW-ONLY and never written anywhere. Both sweeps answered their "what
 * happens tonight" preview with `emailConfigured() ? 'sent' : 'skipped'`: a status computed
 * from configuration instead of from an outcome, which on any deployment with a key read
 * "sent" every time, including the nights when every message bounced. A key that is present
 * is not a key that works — `sendTestEmail` exists because of precisely that gap. So the
 * preview now says the only thing knowable in advance: this one is owed, and it will be
 * attempted. (The other direction still holds: with no key at all, the sender (`lib/email.ts`)
 * returns without attempting anything, so `'skipped'` genuinely is predictable.)
 *
 * `'queued'` (MAP 249 ticket 02) is what a REAL run reports for a step it owed: the email is in
 * the outbox, and the drain sends it and writes what became of it on the ledger row.
 */
export type SweepOutcome = SendOutcome | 'due' | 'queued';

/**
 * One send, turned into the row that records it.
 *
 * This exists so the three tables cannot drift. Before it, each sweep wrote its own ternary:
 * the dunning sweep called every failure `'skipped'` (so nothing was ever reported), and the
 * campaign sender called every failure `'failed'` with the fixed detail *"The mail service
 * would not take it"* — which is a sentence about a service it may never have called. Same
 * mistake, opposite directions, one function apart.
 */
export function deliveryOutcome(result: DeliveryResult): {
  status: SendOutcome;
  detail: string | null;
} {
  if (result.delivered) return { status: 'sent', detail: null };
  return {
    status: countsAsFailure(result.failure) ? 'failed' : 'skipped',
    detail: deliveryProblem(result.failure),
  };
}

/**
 * What happened, in one clause, past tense.
 *
 * Written to the `detail` column of `invoice_reminders` / `quote_reminders` /
 * `email_campaign_recipients` and shown verbatim next to the row, so it has to read as a
 * fact and not as an instruction. The instruction is `deliveryFix`, which the screens that
 * have room for it append.
 *
 * ⚠️ CONTRACT: sentence case, **no trailing punctuation**. Three surfaces glue their own
 * clause onto the end of it (", so nothing was sent.", ", so it didn't send.", ", so
 * nothing was sent — pass this link on yourself."), and a full stop here puts a period in
 * the middle of every one of them. Stated because it was load-bearing and unwritten.
 */
export function deliveryProblem(failure: DeliveryFailure): string {
  switch (failure) {
    case 'no-recipient':
      return 'There’s no email address on file';
    case 'not-configured':
      return 'Email isn’t switched on for this account';
    case 'rejected':
      return 'The mail service turned it down';
    case 'throttled':
      return 'The mail service is holding back messages from this account';
    case 'unreachable':
      // NOT "couldn't reach" and NOT "never answered" — both were wrong. This member also
      // carries a 408 and every 5xx, which ARE answers, and a 2xx whose body named no
      // message. What none of them is, is a CONFIRMATION.
      return 'The mail service didn’t confirm it';
  }
}

/**
 * The next move, addressed to the person reading it.
 *
 * ⚠️ Written in the DETAIL PAGE's voice — a shop looking at one quote or invoice that did
 * not go. Not every surface can use it: the test email's own answer (`SendTestEmail`, Settings →
 * Email) deliberately renders only `deliveryProblem`, because `deliveryFix('rejected')` says
 * "send yourself a test in Settings → Email" and that button IS the test. Check the sentence
 * against the screen before adding another caller.
 *
 * Note what is NOT here: "open the PDF and send it yourself". The detail pages used to say
 * that, and an invoice PDF's `Total` is the invoice total — it does not carry the late fee
 * or a penny of what has already been paid. Telling a shop to send that document, in the
 * exact state where the customer cannot be emailed a correct one, is how a customer gets
 * chased for money they already sent. The customer link renders live and is always right.
 */
export function deliveryFix(failure: DeliveryFailure): string {
  switch (failure) {
    case 'no-recipient':
      return 'Add one to the customer, or send them the link yourself.';
    case 'not-configured':
      return 'Switch it on under Connected services in Settings.';
    case 'rejected':
      // The test lives in Settings → Email ("Send a test to me") since MAP 249 ticket 07: the
      // Connected services card no longer has one (MAP 249 11b QA, D4).
      return 'Send yourself a test in Settings → Email to see what it says.';
    case 'throttled':
      // A 429 is a burst limit OR a plan quota, and this cannot tell which without parsing
      // Resend's structured error type — which is unverifiable from here. So the sentence
      // has to be true of both, rather than promising minutes when it might be a month.
      return 'Check your sending limits, then try again.';
    case 'unreachable':
      // ⚠️ Deliberately NOT "try again". It may already have gone out — see `knownNotSent`.
      return 'It may have gone out anyway, so check with them before sending it again.';
  }
}

/**
 * The `detail` a sweep writes on a row a LATER step supersedes.
 *
 * ⚠️ A magic string with three consumers is a magic string that will disagree with itself.
 * Both sweeps produce these sentences and both preview cards filter on them, so a reword on
 * the server silently makes superseded rows reappear as real work — a claim about tonight
 * that is quietly wrong, which is the exact class ticket 018 exists to remove.
 *
 * ⭐ The better fix is a machine-readable reason code on the row (`reason: 'superseded'`),
 * since the server already ships a structured `status` beside this prose. That is a server
 * contract change and is filed rather than smuggled in here; exporting the literal is the
 * minimum that stops producer and consumers drifting apart in the meantime.
 */
export const SUPERSEDED_FOLLOWUP = 'A later follow-up covers this';
export const SUPERSEDED_REMINDER = 'A later reminder covers this one';
